import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    CircularProgress, Snackbar, Alert, IconButton, Avatar, AvatarGroup, InputAdornment, Tooltip,
    MenuItem, Chip, Tabs, Tab, ToggleButtonGroup, ToggleButton
} from '@mui/material'
import {
    Add, CloudUpload, Close, Search as SearchIcon, Edit, DeleteOutline,
    CheckCircleOutline, CancelOutlined, VisibilityOutlined, VisibilityOffOutlined, Block, CheckCircle
} from '@mui/icons-material'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api, numberWithCommas } from '../../helpers'
import _ from 'lodash'
import { uploadToSpaces, fileToBase64 } from '../../helpers/uploadToSpaces'
import { Connect, mapStateToProps } from '../../Redux'
import { UserProps } from '../../types/user'

type ApprovalStatus = 'Pending Review' | 'Published' | 'Rejected' | 'Unpublished'
type StatusFilter = 'All' | ApprovalStatus
type Condition = 'Brand New' | 'Refurbished' | 'Second Hand'

interface Product {
    _id: string
    name: string
    quantity: number
    description: string
    price: number
    images: string[]
    currency?: string
    itemTypeId?: string
    itemTypeName?: string
    condition?: Condition
    location?: string
    approvalStatus?: ApprovalStatus
    rejectionReason?: string
}

interface ItemType {
    _id: string
    name: string
    isActive: boolean
}

interface FormState {
    name: string
    quantity: string
    description: string
    price: string
    itemTypeId: string
    condition: Condition
    location: string
    imageFiles: File[]
    imagePreviews: string[]
    existingImageUrls: string[]
}

interface StateProps {
    activeTab: 'products' | 'itemTypes'
    products: Product[]
    loading: boolean
    searchVal: string
    statusFilter: StatusFilter
    showModal: boolean
    editingProductId: string | null
    deletingProductId: string | null
    updatingId: string | null
    saving: boolean
    form: FormState
    error: string | null
    previewProduct: Product | null
    rejectingProduct: Product | null
    rejectionReason: string
    itemTypes: ItemType[]
    itemTypesLoading: boolean
    showItemTypeModal: boolean
    editingItemTypeId: string | null
    itemTypeName: string
    itemTypeSaving: boolean
    togglingItemTypeId: string | null
}

const defaultForm: FormState = {
    name: '',
    quantity: '',
    description: '',
    price: '',
    itemTypeId: '',
    condition: 'Brand New',
    location: '',
    imageFiles: [],
    imagePreviews: [],
    existingImageUrls: []
}

const CONDITIONS: Condition[] = ['Brand New', 'Refurbished', 'Second Hand']
const STATUS_FILTERS: StatusFilter[] = ['All', 'Pending Review', 'Published', 'Rejected', 'Unpublished']
const STATUS_COLOR: Record<ApprovalStatus, { bg: string; color: string }> = {
    'Pending Review': { bg: '#fef9c3', color: '#854d0e' },
    'Published': { bg: '#dcfce7', color: '#166534' },
    'Rejected': { bg: '#fee2e2', color: '#991b1b' },
    'Unpublished': { bg: '#f3f4f6', color: '#6b7280' }
}
const headers = ['Name', 'Details', 'Qty', 'Price', 'Images', 'Status', 'Actions']

interface Props {
    User?: UserProps
}

// Now the admin's moderation table for the multi-seller Marketplace, not just its own direct
// catalog entry (see products.ts's getAllProductsAdmin/approveProduct/rejectProduct/etc) - a
// listing here can be either admin-authored (auto-published) or an independent seller's listing
// awaiting review. Item Types get their own tab within the same page since they're a small,
// closely-related piece of config (the picker sellers use when listing), same idea as Installers
// being one self-contained admin page rather than its own top-level section.
const Products: FC<Props> = ({ User }) => {
    const currency = User?.currency ?? 'KES'
    const [state, setState] = useState<StateProps>({
        activeTab: 'products',
        products: [],
        loading: true,
        searchVal: '',
        statusFilter: 'All',
        showModal: false,
        editingProductId: null,
        deletingProductId: null,
        updatingId: null,
        saving: false,
        form: defaultForm,
        error: null,
        previewProduct: null,
        rejectingProduct: null,
        rejectionReason: '',
        itemTypes: [],
        itemTypesLoading: true,
        showItemTypeModal: false,
        editingItemTypeId: null,
        itemTypeName: '',
        itemTypeSaving: false,
        togglingItemTypeId: null
    })
    const {
        activeTab, products, loading, searchVal, statusFilter, showModal, editingProductId, deletingProductId,
        updatingId, saving, form, error, previewProduct, rejectingProduct, rejectionReason,
        itemTypes, itemTypesLoading, showItemTypeModal, editingItemTypeId, itemTypeName, itemTypeSaving, togglingItemTypeId
    } = state
    const fileInputRef = useRef<HTMLInputElement>(null)

    const setForm = (patch: Partial<FormState>) =>
        setState(s => ({ ...s, form: { ...s.form, ...patch } }))

    const getProducts = (search?: string, status: StatusFilter = statusFilter) => {
        setState(s => ({ ...s, loading: true }))
        Api({
            controller: 'Products',
            action: 'getAllProductsAdmin',
            searchVal: search,
            ...(status !== 'All' && { approvalStatus: status })
        })
            .then((resp: any) => setState(s => ({ ...s, products: resp, loading: false })))
            .catch(() => setState(s => ({ ...s, loading: false })))
    }

    const getItemTypes = () => {
        setState(s => ({ ...s, itemTypesLoading: true }))
        Api({ controller: 'MarketItemTypes', action: 'getMarketItemTypes' })
            .then((resp: any) => setState(s => ({ ...s, itemTypes: resp || [], itemTypesLoading: false })))
            .catch(() => setState(s => ({ ...s, itemTypesLoading: false })))
    }

    const debouncedSearch = useCallback(
        _.debounce((val: string) => getProducts(val || undefined), 700),
        [statusFilter]
    )

    const handleStatusFilter = (_: any, val: StatusFilter | null) => {
        if (!val) return
        setState(s => ({ ...s, statusFilter: val }))
        getProducts(searchVal || undefined, val)
    }

    const openAdd = () =>
        setState(s => ({ ...s, showModal: true, editingProductId: null, form: defaultForm }))

    const openEdit = (product: Product) =>
        setState(s => ({
            ...s,
            showModal: true,
            editingProductId: product._id,
            form: {
                name: product.name,
                quantity: String(product.quantity),
                description: product.description ?? '',
                price: String(product.price),
                itemTypeId: product.itemTypeId ?? '',
                condition: product.condition ?? 'Brand New',
                location: product.location ?? '',
                imageFiles: [],
                imagePreviews: [],
                existingImageUrls: product.images ?? []
            }
        }))

    const deleteProduct = (productId: string) => {
        setState(s => ({ ...s, deletingProductId: productId }))
        Api({ controller: 'Products', action: 'deleteProduct', productId })
            .then(() => {
                setState(s => ({ ...s, deletingProductId: null }))
                getProducts()
            })
            .catch((err: any) =>
                setState(s => ({ ...s, deletingProductId: null, error: err?.message || 'Failed to delete product.' }))
            )
    }

    const runModeration = (action: 'approveProduct' | 'publishProduct' | 'unpublishProduct', productId: string) => {
        setState(s => ({ ...s, updatingId: productId }))
        Api({ controller: 'Products', action, productId })
            .then((resp: any) => {
                setState(s => ({ ...s, updatingId: null, ...(resp?.error && { error: resp.error.message }) }))
                if (!resp?.error) getProducts(searchVal || undefined)
            })
            .catch((err: any) => setState(s => ({ ...s, updatingId: null, error: err?.message || 'Something went wrong.' })))
    }

    const confirmReject = () => {
        if (!rejectingProduct || !rejectionReason.trim()) return
        setState(s => ({ ...s, updatingId: rejectingProduct._id }))
        Api({ controller: 'Products', action: 'rejectProduct', productId: rejectingProduct._id, rejectionReason })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, updatingId: null, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, updatingId: null, rejectingProduct: null, rejectionReason: '' }))
                    getProducts(searchVal || undefined)
                }
            })
            .catch((err: any) => setState(s => ({ ...s, updatingId: null, error: err?.message || 'Failed to reject product.' })))
    }

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? [])
        if (!files.length) return
        const previews: string[] = []
        let loaded = 0
        files.forEach(file => {
            const reader = new FileReader()
            reader.onload = ev => {
                previews.push(ev.target?.result as string)
                loaded++
                if (loaded === files.length)
                    setForm({
                        imageFiles: [...form.imageFiles, ...files],
                        imagePreviews: [...form.imagePreviews, ...previews]
                    })
            }
            reader.readAsDataURL(file)
        })
        e.target.value = ''
    }

    const removeNewImage = (i: number) =>
        setForm({
            imageFiles: form.imageFiles.filter((_, idx) => idx !== i),
            imagePreviews: form.imagePreviews.filter((_, idx) => idx !== i)
        })

    const removeExistingImage = (i: number) =>
        setForm({ existingImageUrls: form.existingImageUrls.filter((_, idx) => idx !== i) })

    const saveProduct = async () => {
        setState(s => ({ ...s, saving: true }))
        try {
            const base64Files = await Promise.all(form.imageFiles.map(fileToBase64))
            const newUrls = await uploadToSpaces(base64Files, 'products')
            const images = [...form.existingImageUrls, ...newUrls]
            const itemTypeName = itemTypes.find(t => t._id === form.itemTypeId)?.name

            const resp: any = await Api({
                controller: 'Products',
                action: editingProductId ? 'updateProduct' : 'addProduct',
                ...(editingProductId && { productId: editingProductId }),
                name: form.name,
                quantity: Number(form.quantity),
                description: form.description,
                price: Number(form.price),
                itemTypeId: form.itemTypeId,
                itemTypeName,
                condition: form.condition,
                location: form.location,
                currency,
                images
            })

            if (resp?.error)
                setState(s => ({ ...s, saving: false, error: resp.error.message }))
            else {
                setState(s => ({ ...s, saving: false, showModal: false, editingProductId: null, form: defaultForm }))
                getProducts(searchVal || undefined)
            }
        } catch (err: any) {
            setState(s => ({ ...s, saving: false, error: err?.message || 'Something went wrong.' }))
        }
    }

    const canSave = !saving && !!form.name && !!form.quantity && !!form.price && !!form.itemTypeId && !!form.location

    // Item Types tab
    const openAddItemType = () => setState(s => ({ ...s, showItemTypeModal: true, editingItemTypeId: null, itemTypeName: '' }))
    const openEditItemType = (t: ItemType) => setState(s => ({ ...s, showItemTypeModal: true, editingItemTypeId: t._id, itemTypeName: t.name }))

    const saveItemType = () => {
        setState(s => ({ ...s, itemTypeSaving: true }))
        const payload = editingItemTypeId ? { itemTypeId: editingItemTypeId, name: itemTypeName } : { name: itemTypeName }
        Api({ controller: 'MarketItemTypes', action: editingItemTypeId ? 'updateMarketItemType' : 'addMarketItemType', ...payload })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, itemTypeSaving: false, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, itemTypeSaving: false, showItemTypeModal: false, editingItemTypeId: null, itemTypeName: '' }))
                    getItemTypes()
                }
            })
            .catch((err: any) => setState(s => ({ ...s, itemTypeSaving: false, error: err?.message || 'Something went wrong.' })))
    }

    const toggleItemTypeActive = (t: ItemType) => {
        setState(s => ({ ...s, togglingItemTypeId: t._id }))
        Api({ controller: 'MarketItemTypes', action: t.isActive ? 'deactivateMarketItemType' : 'reactivateMarketItemType', itemTypeId: t._id })
            .then(() => {
                setState(s => ({ ...s, togglingItemTypeId: null }))
                getItemTypes()
            })
            .catch((err: any) => setState(s => ({ ...s, togglingItemTypeId: null, error: err?.message || 'Failed to update item type.' })))
    }

    useEffect(() => { getProducts(); getItemTypes() }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Products</Typography>
                <Typography sx={{ color: '#6b7280', fontSize: 14 }}>
                    Independent sellers submit listings for review here; only Published listings appear on the Marketplace.
                </Typography>
            </Box>

            <Tabs
                value={activeTab}
                onChange={(_, val) => setState(s => ({ ...s, activeTab: val }))}
                sx={{ px: '5px', mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600 } }}
            >
                <Tab label='Products' value='products' />
                <Tab label='Item Types' value='itemTypes' />
            </Tabs>

            {activeTab === 'products' ? (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '5px', mb: 1, flexWrap: 'wrap' }}>
                        <TextField
                            size='small'
                            placeholder='Search products...'
                            value={searchVal}
                            onChange={e => {
                                const val = e.target.value
                                setState(s => ({ ...s, searchVal: val }))
                                debouncedSearch(val)
                            }}
                            sx={{ flex: 1, minWidth: 200, bgcolor: '#fff', borderRadius: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button
                            variant='contained'
                            startIcon={<Add />}
                            onClick={openAdd}
                            sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                        >
                            Add Product
                        </Button>
                    </Box>

                    <Box sx={{ px: '5px', mb: 2 }}>
                        <ToggleButtonGroup
                            value={statusFilter}
                            exclusive
                            onChange={handleStatusFilter}
                            size='small'
                            sx={{ bgcolor: '#fff', borderRadius: 2, flexWrap: 'wrap' }}
                        >
                            {STATUS_FILTERS.map(f => (
                                <ToggleButton
                                    key={f}
                                    value={f}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 2,
                                        '&.Mui-selected': {
                                            bgcolor: 'primary.main',
                                            color: '#fff',
                                            '&:hover': { bgcolor: 'primary.dark' }
                                        }
                                    }}
                                >
                                    {f}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    <Show>
                        <Show.When isTrue={loading}>
                            <EmptyView loading={loading} oopTxt='No Products' text='No products found.' />
                        </Show.When>
                        <Show.Else>
                            <TableContainer component={Paper} sx={{ mt: 1, mx: '5px' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                                            {headers.map(h => (
                                                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {products.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={headers.length} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                                    No products yet. Add your first one.
                                                </TableCell>
                                            </TableRow>
                                        ) : products.map(product => {
                                            const status = product.approvalStatus || 'Published'
                                            return (
                                                <TableRow key={product._id} hover>
                                                    <TableCell sx={{ fontWeight: 600 }}>{product.name}</TableCell>
                                                    <TableCell>
                                                        <Typography sx={{ fontSize: '0.8rem', color: '#374151' }}>{product.itemTypeName || '—'}</Typography>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>{product.condition || '—'} · {product.location || '—'}</Typography>
                                                    </TableCell>
                                                    <TableCell>{numberWithCommas(product.quantity)}</TableCell>
                                                    <TableCell>{product.currency ?? currency} {numberWithCommas(product.price)}</TableCell>
                                                    <TableCell>
                                                        {product.images?.length ? (
                                                            <Box
                                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                                                                onClick={() => setState(s => ({ ...s, previewProduct: product }))}
                                                            >
                                                                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.65rem', border: '2px solid #fff' } }}>
                                                                    {product.images.map((src, i) => (
                                                                        <Avatar key={i} src={src} variant='rounded' sx={{ width: 32, height: 32 }} />
                                                                    ))}
                                                                </AvatarGroup>
                                                            </Box>
                                                        ) : (
                                                            <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title={status === 'Rejected' && product.rejectionReason ? product.rejectionReason : ''}>
                                                            <Chip
                                                                label={status}
                                                                size='small'
                                                                sx={{ bgcolor: STATUS_COLOR[status].bg, color: STATUS_COLOR[status].color, fontWeight: 600, fontSize: '0.75rem' }}
                                                            />
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        {status === 'Pending Review' && (
                                                            <>
                                                                <Tooltip title='Approve & Publish'>
                                                                    <IconButton
                                                                        size='small'
                                                                        onClick={() => runModeration('approveProduct', product._id)}
                                                                        disabled={updatingId === product._id}
                                                                        sx={{ color: '#16a34a' }}
                                                                    >
                                                                        {updatingId === product._id ? <CircularProgress size={14} color='inherit' /> : <CheckCircleOutline fontSize='small' />}
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title='Reject'>
                                                                    <IconButton
                                                                        size='small'
                                                                        onClick={() => setState(s => ({ ...s, rejectingProduct: product, rejectionReason: '' }))}
                                                                        disabled={updatingId === product._id}
                                                                        sx={{ color: '#ef4444', ml: 0.5 }}
                                                                    >
                                                                        <CancelOutlined fontSize='small' />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                        {status === 'Published' && (
                                                            <Tooltip title='Unpublish'>
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={() => runModeration('unpublishProduct', product._id)}
                                                                    disabled={updatingId === product._id}
                                                                    sx={{ color: '#6b7280' }}
                                                                >
                                                                    {updatingId === product._id ? <CircularProgress size={14} color='inherit' /> : <VisibilityOffOutlined fontSize='small' />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {status === 'Unpublished' && (
                                                            <Tooltip title='Publish'>
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={() => runModeration('publishProduct', product._id)}
                                                                    disabled={updatingId === product._id}
                                                                    sx={{ color: 'primary.main' }}
                                                                >
                                                                    {updatingId === product._id ? <CircularProgress size={14} color='inherit' /> : <VisibilityOutlined fontSize='small' />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title='Edit'>
                                                            <IconButton size='small' onClick={() => openEdit(product)} sx={{ color: 'primary.main', ml: 0.5 }}>
                                                                <Edit fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title='Delete'>
                                                            <IconButton
                                                                size='small'
                                                                onClick={() => deleteProduct(product._id)}
                                                                disabled={deletingProductId === product._id}
                                                                sx={{ color: '#ef4444', ml: 0.5 }}
                                                            >
                                                                {deletingProductId === product._id
                                                                    ? <CircularProgress size={14} color='inherit' />
                                                                    : <DeleteOutline fontSize='small' />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Show.Else>
                    </Show>
                </>
            ) : (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: '5px', mb: 1 }}>
                        <Button variant='contained' startIcon={<Add />} onClick={openAddItemType} sx={{ borderRadius: 2 }}>
                            Add Item Type
                        </Button>
                    </Box>
                    <Show>
                        <Show.When isTrue={itemTypesLoading}>
                            <EmptyView loading={itemTypesLoading} oopTxt='No Item Types' text='No item types found.' />
                        </Show.When>
                        <Show.Else>
                            <TableContainer component={Paper} sx={{ mt: 1, mx: '5px' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                                            {['Name', 'Status', 'Actions'].map(h => (
                                                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {itemTypes.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                                    No item types yet. Add your first one.
                                                </TableCell>
                                            </TableRow>
                                        ) : itemTypes.map(t => (
                                            <TableRow key={t._id} hover>
                                                <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={t.isActive ? 'Active' : 'Inactive'}
                                                        size='small'
                                                        sx={{ bgcolor: t.isActive ? '#dcfce7' : '#f3f4f6', color: t.isActive ? '#166534' : '#6b7280', fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title='Edit'>
                                                        <IconButton size='small' onClick={() => openEditItemType(t)} sx={{ color: 'primary.main' }}>
                                                            <Edit fontSize='small' />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t.isActive ? 'Deactivate' : 'Reactivate'}>
                                                        <IconButton
                                                            size='small'
                                                            onClick={() => toggleItemTypeActive(t)}
                                                            disabled={togglingItemTypeId === t._id}
                                                            sx={{ color: t.isActive ? '#ef4444' : '#166534', ml: 0.5 }}
                                                        >
                                                            {togglingItemTypeId === t._id
                                                                ? <CircularProgress size={14} color='inherit' />
                                                                : t.isActive ? <Block fontSize='small' /> : <CheckCircle fontSize='small' />}
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Show.Else>
                    </Show>
                </>
            )}

            {/* Add / Edit Product Dialog */}
            <Dialog open={showModal} onClose={() => !saving && setState(s => ({ ...s, showModal: false }))} fullWidth maxWidth='sm'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingProductId ? 'Edit Product' : 'Add Product'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        label='Title'
                        value={form.name}
                        onChange={e => setForm({ name: e.target.value })}
                        fullWidth
                        size='small'
                    />
                    <TextField
                        select
                        label='Item Type'
                        value={form.itemTypeId}
                        onChange={e => setForm({ itemTypeId: e.target.value })}
                        fullWidth
                        size='small'
                    >
                        {itemTypes.map(t => (
                            <MenuItem key={t._id} value={t._id}>{t.name}{!t.isActive ? ' (inactive)' : ''}</MenuItem>
                        ))}
                    </TextField>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            select
                            label='Condition'
                            value={form.condition}
                            onChange={e => setForm({ condition: e.target.value as Condition })}
                            fullWidth
                            size='small'
                        >
                            {CONDITIONS.map(c => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label='Location'
                            value={form.location}
                            onChange={e => setForm({ location: e.target.value })}
                            fullWidth
                            size='small'
                            placeholder='e.g. Nairobi, Kenya'
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label='Quantity in Stock'
                            type='number'
                            value={form.quantity}
                            onChange={e => setForm({ quantity: e.target.value })}
                            fullWidth
                            size='small'
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label='Price'
                            type='number'
                            value={form.price}
                            onChange={e => setForm({ price: e.target.value })}
                            fullWidth
                            size='small'
                            inputProps={{ min: 0 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontWeight: 600 }}>
                                            {currency}
                                        </Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                    <TextField
                        label='Description'
                        value={form.description}
                        onChange={e => setForm({ description: e.target.value })}
                        fullWidth
                        size='small'
                        multiline
                        rows={3}
                    />

                    {/* Image Upload */}
                    <Box>
                        <input
                            ref={fileInputRef}
                            type='file'
                            accept='image/*'
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleImageSelect}
                        />
                        <Button
                            variant='outlined'
                            startIcon={<CloudUpload />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ borderRadius: 2, borderStyle: 'dashed', textTransform: 'none' }}
                            fullWidth
                        >
                            Upload Images
                        </Button>

                        {(form.existingImageUrls.length > 0 || form.imagePreviews.length > 0) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                                {form.existingImageUrls.map((src, i) => (
                                    <Box key={`existing-${i}`} sx={{ position: 'relative' }}>
                                        <Avatar src={src} variant='rounded' sx={{ width: 60, height: 60 }} />
                                        <IconButton
                                            size='small'
                                            onClick={() => removeExistingImage(i)}
                                            sx={{ position: 'absolute', top: -6, right: -6, bgcolor: '#ef4444', color: '#fff', width: 18, height: 18, '&:hover': { bgcolor: '#dc2626' } }}
                                        >
                                            <Close sx={{ fontSize: 12 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                                {form.imagePreviews.map((src, i) => (
                                    <Box key={`new-${i}`} sx={{ position: 'relative' }}>
                                        <Avatar src={src} variant='rounded' sx={{ width: 60, height: 60 }} />
                                        <IconButton
                                            size='small'
                                            onClick={() => removeNewImage(i)}
                                            sx={{ position: 'absolute', top: -6, right: -6, bgcolor: '#ef4444', color: '#fff', width: 18, height: 18, '&:hover': { bgcolor: '#dc2626' } }}
                                        >
                                            <Close sx={{ fontSize: 12 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, showModal: false }))} disabled={saving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={saveProduct}
                        disabled={!canSave}
                        startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        {editingProductId ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={Boolean(rejectingProduct)} onClose={() => !updatingId && setState(s => ({ ...s, rejectingProduct: null }))} fullWidth maxWidth='xs'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Reject Listing</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 1.5 }}>
                        The seller will see this reason on "{rejectingProduct?.name}".
                    </Typography>
                    <TextField
                        label='Reason for rejection'
                        value={rejectionReason}
                        onChange={e => setState(s => ({ ...s, rejectionReason: e.target.value }))}
                        fullWidth
                        size='small'
                        multiline
                        rows={2}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, rejectingProduct: null }))} disabled={Boolean(updatingId)}>Back</Button>
                    <Button
                        variant='contained'
                        color='error'
                        onClick={confirmReject}
                        disabled={!rejectionReason.trim() || Boolean(updatingId)}
                        startIcon={updatingId ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        Confirm Reject
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Item Type Add/Edit Dialog */}
            <Dialog open={showItemTypeModal} onClose={() => !itemTypeSaving && setState(s => ({ ...s, showItemTypeModal: false }))} fullWidth maxWidth='xs'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>{editingItemTypeId ? 'Edit Item Type' : 'Add Item Type'}</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <TextField
                        label='Name'
                        value={itemTypeName}
                        onChange={e => setState(s => ({ ...s, itemTypeName: e.target.value }))}
                        fullWidth
                        size='small'
                        placeholder='e.g. Cue Sticks'
                        autoFocus
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, showItemTypeModal: false }))} disabled={itemTypeSaving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={saveItemType}
                        disabled={!itemTypeName.trim() || itemTypeSaving}
                        startIcon={itemTypeSaving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        {editingItemTypeId ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Preview Dialog */}
            <Dialog
                open={Boolean(previewProduct)}
                onClose={() => setState(s => ({ ...s, previewProduct: null }))}
                maxWidth='sm'
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {previewProduct?.name}
                    <IconButton onClick={() => setState(s => ({ ...s, previewProduct: null }))} size='small'>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {previewProduct?.images.map((src, i) => (
                            <Box
                                key={i}
                                component='img'
                                src={src}
                                alt={`${previewProduct.name} ${i + 1}`}
                                sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 2, border: '1px solid #e5e7eb' }}
                            />
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Connect(mapStateToProps)(Products)
